import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import axios from 'axios';
import { Calendar, ChevronRight, Plus, RotateCcw, Archive } from 'lucide-react';
import { getCurrencySymbol } from '../utils/currency';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCancelledDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

export default function AllDebits() {
  const [debits, setDebits] = useState([]);
  const [cancelled, setCancelled] = useState([]);
  const [tab, setTab] = useState('active'); // 'active' | 'cancelled'
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [debitsRes, cancelledRes, subRes] = await Promise.all([
        axios.get(`${API_URL}/api/direct-debits`),
        axios.get(`${API_URL}/api/direct-debits/cancelled`),
        axios.get(`${API_URL}/api/subscription/status`),
      ]);
      setDebits(debitsRes.data);
      setCancelled(cancelledRes.data);
      setSubscription(subRes.data);
    } catch (err) {
      console.error('Error fetching debits:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (id) => {
    setRestoringId(id);
    try {
      await axios.post(`${API_URL}/api/direct-debits/${id}/restore`);
      toast.success('Debit restored to active');
      await fetchData();
      setTab('active');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not restore debit';
      toast.error(msg);
    } finally {
      setRestoringId(null);
    }
  };

  const isPremium = subscription?.tier === 'premium';

  if (loading) {
    return (
      <PageLayout showNav showUpgrade isPremium={isPremium}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout showNav showUpgrade isPremium={isPremium}>
      <div className="px-4 py-6 max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-heading font-bold text-dark text-2xl">All Direct Debits</h1>
          <Link
            to="/add"
            data-testid="add-debit-btn"
            className="btn-primary p-2 rounded-xl"
          >
            <Plus className="w-5 h-5" />
          </Link>
        </div>

        {/* Tabs */}
        <div className="inline-flex bg-primary-light rounded-full p-1 mb-5" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'active'}
            onClick={() => setTab('active')}
            data-testid="tab-active"
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === 'active' ? 'bg-primary text-white' : 'text-primary'}`}
          >
            Active <span className="ml-1 opacity-75">({debits.length})</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'cancelled'}
            onClick={() => setTab('cancelled')}
            data-testid="tab-cancelled"
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === 'cancelled' ? 'bg-primary text-white' : 'text-primary'}`}
          >
            Cancelled <span className="ml-1 opacity-75">({cancelled.length})</span>
          </button>
        </div>

        {/* ACTIVE TAB */}
        {tab === 'active' && (
          <>
            {debits.length > 0 ? (
              <div className="space-y-3">
                {debits.map((debit) => (
                  <Link
                    key={debit.id}
                    to={`/edit/${debit.id}`}
                    className="card-default p-4 flex items-center gap-4 hover:-translate-y-0.5 hover:shadow-md transition-all"
                    data-testid={`debit-item-${debit.id}`}
                  >
                    <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-dark truncate">{debit.company_name}</p>
                      <p className="text-sm text-text-light capitalize">
                        {debit.frequency} • Day {debit.payment_day}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="font-heading font-bold text-dark">{getCurrencySymbol(debit.currency)}{debit.amount.toFixed(2)}</p>
                        <p className="text-xs text-text-light">{debit.next_payment_date}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-text-light" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="card-default p-8 text-center">
                <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-dark text-lg mb-2">
                  No direct debits yet
                </h3>
                <p className="text-text-secondary mb-6">
                  Start tracking your payments to stay in control
                </p>
                <Link
                  to="/add"
                  data-testid="add-first-debit-btn"
                  className="btn-primary inline-block px-6 py-3"
                >
                  Add Your First Direct Debit
                </Link>
              </div>
            )}

            {debits.length > 0 && (
              <div className="mt-6 p-4 bg-primary-light rounded-2xl">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Total Monthly (approx)</span>
                  <span className="font-heading font-bold text-dark text-lg">
                    {getCurrencySymbol(debits[0]?.currency)}{debits.reduce((sum, d) => sum + d.amount, 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* CANCELLED TAB */}
        {tab === 'cancelled' && (
          <>
            <p className="text-xs text-text-light mb-3 leading-relaxed">
              A record of debits you've marked as cancelled in MDD. Kept for your reference.
              <span className="block mt-1">Restoring brings a debit back to your active list — it does not contact your bank.</span>
              <span className="block mt-2 font-medium text-text-secondary">Reminder: to fully stop a debit, contact your bank <strong>and</strong> delete the recurring reminder from your phone/Google/Outlook calendar.</span>
            </p>

            {cancelled.length > 0 ? (
              <div className="space-y-3" data-testid="cancelled-list">
                {cancelled.map((debit) => (
                  <div
                    key={debit.id}
                    className="card-default p-4 flex items-start gap-4 opacity-95"
                    data-testid={`cancelled-item-${debit.id}`}
                  >
                    <div className="w-12 h-12 bg-page rounded-xl flex items-center justify-center border border-border">
                      <Archive className="w-6 h-6 text-text-light" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-dark truncate">{debit.company_name}</p>
                      <p className="text-sm text-text-light capitalize">
                        {debit.frequency} • Day {debit.payment_day}
                      </p>
                      <p className="text-xs text-text-light mt-1">
                        Cancelled in-app on {formatCancelledDate(debit.cancelled_at)}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="font-heading font-bold text-dark">{getCurrencySymbol(debit.currency)}{debit.amount.toFixed(2)}</p>
                      <button
                        type="button"
                        onClick={() => handleRestore(debit.id)}
                        disabled={restoringId === debit.id}
                        data-testid={`restore-btn-${debit.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary text-white hover:bg-primary-hover disabled:opacity-60 transition-colors"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {restoringId === debit.id ? 'Restoring…' : 'Restore'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="card-default p-8 text-center">
                <div className="w-16 h-16 bg-page rounded-full flex items-center justify-center mx-auto mb-4 border border-border">
                  <Archive className="w-8 h-8 text-text-light" />
                </div>
                <h3 className="font-heading font-semibold text-dark text-lg mb-2">
                  No cancelled debits
                </h3>
                <p className="text-text-secondary text-sm">
                  Anything you mark as cancelled in MDD will be kept here for your reference.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </PageLayout>
  );
}
